import React, { useState, useEffect, useContext, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import 'dayjs/locale/ja'
import 'react-dates/initialize'
import { AccountContext } from '@/contexts/account'
import { UnsavedChangesRouteGuard } from '@/components/molecules/unsaved-changes-route-guard'
import { useReservationEditingSections } from '@/hooks/use-reservation-editing-sections'
import { setHasRepeatPlugin, setHasSalesManagerPlugin, hasSpaceVisibility } from '@/libs/plugins'
import {
  wholeContainerStyle,
  mainColumnStyle,
  checkinDetailContainerStyle,
  reservationDetailContainerStyle,
  leftContainerStyleForReservation,
  rightContainerStyleForReservation,
} from '@/constants/layout'
import * as api from '@/apis/aipass'
import { Button } from '@/components/atoms/button'
import { Header } from '@/components/organisms/header'
import { SideMenu } from '@/components/organisms/side-menu'
import { LoadingFull } from '@/components/molecules/loading-full'
import { Sales } from '@/components/organisms/reservation/sales'
import { ReservationUserEditModal } from '@/components/pages/accommodation-management/reservation/reservation-user-edit/reservation-user-edit-modal'
import { ReservationUserEditForm } from '@/components/pages/accommodation-management/reservation/reservation-user-edit/reservation-user-edit-form'
import { SuggestCustomer } from '@/components/organisms/accommodation-management/suggest-customer'
import { CustomerLink } from '@/components/organisms/accommodation-management/customer-link'
import { SearchCustomerLinkButton } from '@/components/organisms/accommodation-management/search-customer-link-button'
import { SearchCustomerLinkModal } from '@/components/organisms/search-customer-link-modal'
import { DetailEditInfo } from '@/components/organisms/reservation/detail-edit-info'
import { GuestRoom } from '@/components/organisms/customer/guest-room'
import { ReservationList } from '@/components/pages/accommodation-management/reservation/reservation-list/reservation-list'
import { ReservationDetailMemo } from '@/components/organisms/reservation/reservation-detail-memo'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { CustomerType } from '@/models/customer-manager/customer'
import { Checkbox } from '@/components/atoms/checkbox'
import { useSameTabsReload } from '@/hooks/use-same-tabs-reload'
import { ListSpaceForReservation } from '@/components/organisms/customer/list-space-for-reservation'
import qs from 'qs'
import { PlanInfoDetail } from '@/components/organisms/customer/_plan-info/plan-info-detail'
import { useBrowserUnloadGuard } from '@/hooks/use-browser-unload-guard'

export const ReservationDetail: React.FC = () => {
  const { t } = useTranslation()
  const { errorHandler } = useErrorHandler()
  const history = useHistory()
  const params = qs.parse(window.location.search.substring(1))
  const [reservation, setReservation] = useState<any>()
  const [isReservationUserEditModalOpen, setIsReservationUserEditModalOpen] = useState<boolean>(false)
  const [isReservationUserEditModalLoading, setIsReservationUserEditModalLoading] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isEdit, setIsEdit] = useState<boolean>(false)
  const [salesReloadKey, setSalesReloadKey] = useState<number>(0)
  const { plugins } = useContext<any>(AccountContext)
  const hasSalesManagerPlugin = setHasSalesManagerPlugin(plugins)
  const [hasConfirmedSales, setHasConfirmedSales] = useState<boolean>(false)
  const [hasConfirmedDiscount, setHasConfirmedDiscount] = useState<boolean>(false)
  const [reflectCustomerDataToUser, setReflectCustomerDataToUser] = useState<boolean>(false)
  const [reflectCustomerDataToGuest, setReflectCustomerDataToGuest] = useState<boolean>(false)

  // Customer management related (no repeat management plugin/plugin)
  // Display of customer search modal (before linking)
  const [isOpenSearchCustomerLinkModal, setIsOpenSearchCustomerLinkModal] = useState<boolean>(false)
  // Linked customer (after linking)
  const [customerlink, setCustomerLink] = useState<CustomerType>()

  // Assisted customer list (before pegging)
  const [customers, setCustomers] = useState<CustomerType[]>([])
  // Editing tags (after binding)
  const [isEditTag, setIsEditTag] = useState<boolean>(false)
  // Tag value (after binding)
  const [tags, setTags] = useState<string[]>([])

  // Whether it is a repeat management plugin
  const hasRepeatPlugin = setHasRepeatPlugin(plugins)
  const shouldShowSpace = useMemo(() => hasSpaceVisibility(plugins), [plugins])

  // 編集されているセクションがあるか、そのセクション名
  const { hasUnsaved, unsavedLabels } = useReservationEditingSections()
  // ブラウザバックやリロード、別タブへの移動などの前に確認ダイアログを出す
  useBrowserUnloadGuard(hasUnsaved)

  const { reloadSameTabs } = useSameTabsReload({ key: params.reservationId })

  useEffect(() => {
    fetchReservation()
    if (params.reservationId) {
      api.updateAdminReservationRead(params.reservationId).catch(() => {
        // 既読化失敗（スタッフセッション未確立等）は画面表示を妨げない
      })
    }
  }, [])

  // 再取得のたびに reservation は別オブジェクトになるため、検索条件の電話番号が変わったときだけ検索する
  const guestTel: string | undefined = reservation?.guestTel
  const userTel: string | undefined = reservation?.userTel
  useEffect(() => {
    fetchCustomerSearch()
  }, [guestTel, userTel])

  /**
   * 予約詳細を取得し、関連ステートを更新する。
   * ステータスに応じて該当画面へリダイレクトする。
   */
  const fetchReservation = async () => {
    try {
      setIsLoading(true)
      const { reservationId } = params
      await api.fetchReservationDetail({ reservationId }).then(res => {
        if (!res) return console.warn('fetchReservation return empry response')

        const reservation = res.reservations

        if (reservation.approvedStatus === 1) {
          history.replace(`/checkin/${reservation.checkinId}`)
        } else if (reservation.approvedStatus === 2) {
          history.replace(`/stay/${reservation.checkinId}`)
        } else if (reservation.approvedStatus === 3) {
          history.replace(`/checkout/${reservation.checkinId}`)
        }

        setReservation(reservation)
        setCustomerLink(res.customer)
        setTags(res.customer.tagInfo?.tags.map(tag => tag.id))
      })
    } catch (error) {
      console.log(t('Communication failed'))
    } finally {
      setIsLoading(false)
    }
  }

  /**
   * 電話番号で顧客情報を検索し、顧客リストを更新する。
   * 電話番号が空または "0" の場合は処理をスキップする。
   */
  const fetchCustomerSearch = async () => {
    if ((!reservation?.guestTel || reservation.guestTel === '0') && (!reservation?.userTel || reservation.userTel === '0')) {
      setCustomers([])
      return
    }
    const guestTelephone = reservation?.guestTel === '0' ? '' : reservation?.guestTel
    const userTelephone = reservation?.userTel === '0' ? '' : reservation?.userTel
    await api
      .fetchCustomerSearch({ guestTelephone, userTelephone, page: 1, limit: 100 })
      .then(res => {
        setCustomers(res?.hotelCuicinUsers)
      })
      .catch(() => {
        console.log(t('Communication failed'))
      })
  }

  const onSaveCustomerLink = async (hotelCuicinUserId: string) => {
    setIsLoading(true)
    await api.createCustomerLink('', reservation.reservationId, hotelCuicinUserId)
    fetchReservation()
    setIsOpenSearchCustomerLinkModal(false)
    reloadSameTabs()
    setIsLoading(false)
  }

  // Customer unlinking (customer management)
  const deleteCustomerLink = async () => {
    setIsLoading(true)
    await api.deleteCustomerLink('', reservation.reservationId)
    setCustomerLink(undefined)
    reloadSameTabs()
    setIsLoading(false)
  }

  const handleClickReservationUserEdittButton = () => {
    setIsReservationUserEditModalOpen(true)
  }
  const handleClickReservationUserEditSaveButton = async (form: ReservationUserEditForm) => {
    setIsReservationUserEditModalLoading(true)
    try {
      await api.putAdminReservationUser(reservation.reservationId, {
        reservationUser: form.getReservationUser(),
        reservationGuest: form.getReservationGuest(),
      })
      const res = await api.fetchReservationDetail({ reservationId: reservation.reservationId })
      setCustomerLink(res?.customer)
      setTags(res?.customer.tagInfo?.tags.map(tag => tag.id))
      setReservation(res?.reservations)
      setIsReservationUserEditModalOpen(false)
      reloadSameTabs()
    } finally {
      setIsReservationUserEditModalLoading(false)
    }
  }
  const handleClickReservationUserEditCancelButton = () => {
    setIsReservationUserEditModalOpen(false)
  }

  const resetCopyCustomerData = () => {
    setReflectCustomerDataToUser(false)
    setReflectCustomerDataToGuest(false)
  }

  const copyCustomerData = async () => {
    try {
      setIsLoading(true)

      await api.userGuestCopy({
        isReservationUserCopy: reflectCustomerDataToUser,
        isReservationGuestCopy: reflectCustomerDataToGuest,
        hotelCuicinUserId: customerlink?.id,
        reservationId: reservation.reservationId,
      })
      await fetchReservation()

      resetCopyCustomerData()
      reloadSameTabs()
    } catch (error) {
      errorHandler(error)
    } finally {
      setIsLoading(false)
    }
  }

  const onClickCancelTag = () => {
    setIsEditTag(false)
    setTags(customerlink?.tagInfo?.tags?.map(tagInfo => tagInfo.id) || [])
  }

  const showEditHeader = reflectCustomerDataToUser || reflectCustomerDataToGuest

  const getUserNameKana = () => {
    if (reflectCustomerDataToUser) return customerlink?.nameKana || customerlink?.name_kana || '-'

    return reservation && reservation.userNameKana ? reservation.userNameKana : '-'
  }

  const getUserName = () => {
    if (reflectCustomerDataToUser) return customerlink?.name || '-'

    return reservation && reservation.userName ? reservation.userName : '-'
  }

  const getUserTel = () => {
    if (reflectCustomerDataToUser) return customerlink?.telephone || '-'

    return reservation && reservation.userTel ? reservation.userTel : '-'
  }

  const getUserEmail = () => {
    if (reflectCustomerDataToUser) return customerlink?.email || '-'

    return reservation && reservation.userMailAddr ? reservation.userMailAddr : '-'
  }

  const getUserAddr = () => {
    if (reflectCustomerDataToUser) return customerlink?.address || '-'

    return reservation && reservation.userAddr ? reservation.userAddr : '-'
  }

  const getGuestNameKana = () => {
    if (reflectCustomerDataToGuest) return customerlink?.nameKana || customerlink?.name_kana || '-'

    return reservation && reservation.guestNameKana ? reservation.guestNameKana : '-'
  }

  const getGuestName = () => {
    if (reflectCustomerDataToGuest) return customerlink?.name || '-'

    return reservation && reservation.guestName ? reservation.guestName : '-'
  }

  const getGuestTel = () => {
    if (reflectCustomerDataToGuest) return customerlink?.telephone || '-'

    return reservation && reservation.guestTel ? reservation.guestTel : '-'
  }

  const getGuestEmail = () => {
    if (reflectCustomerDataToGuest) return customerlink?.email || '-'

    return reservation && reservation.guestMailAddr ? reservation.guestMailAddr : '-'
  }

  const getGuestAddr = () => {
    if (reflectCustomerDataToGuest) return customerlink?.address || '-'

    return reservation && reservation.guestAddr ? reservation.guestAddr : '-'
  }

  return (
    <div css={wholeContainerStyle}>
      <SideMenu />
      <div className="mainColumn" css={mainColumnStyle}>
        <Header isBack title={t('Reservation Details')} goBack={history.goBack} />
        <div className="mainContainer" style={{ padding: 24 }}>
          <div css={checkinDetailContainerStyle}>
            <div css={leftContainerStyleForReservation}>
              <div>
                <div css={guestInfoContainerStyle}>
                  <div css={{ fontSize: 16, fontWeight: 'bold', color: '#272727' }}>{t('Guest information')}</div>
                  <div css={{ display: 'flex' }}>
                    {showEditHeader ? (
                      <div css={{ display: 'flex', alignItems: 'center' }}>
                        <Button buttonType={3} width={100} height={30} fontSize={12} marginRight={16} onClick={resetCopyCustomerData}>
                          {t('Cancel')}
                        </Button>
                        <Button buttonType={1} width={100} height={30} fontSize={12} onClick={copyCustomerData}>
                          {t('Save')}
                        </Button>
                      </div>
                    ) : (
                      <div css={[editableIcon, { marginLeft: 16 }]} onClick={handleClickReservationUserEdittButton}></div>
                    )}
                  </div>
                </div>
                <div css={{ height: '100%' }}>
                  <div css={[leftTopSectionStyle, { height: '100%' }]}>
                    <div css={[detailCustomerContainerStyle, { borderRight: '1px solid #F2F2F2' }]}>
                      <div css={nameAndReflectCustomerStyle}>
                        <div css={reservationDetailLeftTitleStyle}>{t('Reservation name')}</div>
                        <Checkbox
                          value={reflectCustomerDataToUser}
                          onChange={setReflectCustomerDataToUser}
                          label={t('Reflect customer data')}
                          disabled={!customerlink}
                        />
                      </div>
                      <div css={{ display: 'flex' }}>
                        <div css={reservationDetailLeftSectionStyle}>
                          <div css={reservationDetailLeftNameKanaStyle}>{getUserNameKana()}</div>
                          <div css={reservationDetailLeftNameStyle}>{getUserName()}</div>
                        </div>
                        <ul css={guestItemContainerStyle}>
                          <li css={guestItemListContainerStyle}>
                            <img alt="tell" src={require('@/static/images/tell.svg')} />
                            <div css={guestItemStyle}>{getUserTel()}</div>
                          </li>
                          <li css={guestItemListContainerStyle}>
                            <img alt="mail" src={require('@/static/images/mail.svg')} />
                            <div css={guestItemStyle}>{getUserEmail()}</div>
                          </li>
                          <li css={guestItemListLastContainerStyle}>
                            <img alt="access" src={require('@/static/images/access.svg')} />
                            <div css={guestItemStyle}>{getUserAddr()}</div>
                          </li>
                        </ul>
                      </div>
                    </div>
                    <div css={[detailCustomerContainerStyle]}>
                      <div css={nameAndReflectCustomerStyle}>
                        <div css={reservationDetailLeftTitleStyle}>{t('Guest name')}</div>
                        <Checkbox
                          value={reflectCustomerDataToGuest}
                          onChange={setReflectCustomerDataToGuest}
                          label={t('Reflect customer data')}
                          disabled={!customerlink}
                        />
                      </div>
                      <div css={{ display: 'flex' }}>
                        <div css={reservationDetailLeftSectionStyle}>
                          <div css={reservationDetailLeftguestNameKanaStyle}>{getGuestNameKana()}</div>
                          <div css={reservationDetailLeftguestNameStyle}>{getGuestName()}</div>
                        </div>
                        {/* {isListOpen && ( */}
                        <ul css={guestItemContainerStyle}>
                          <li css={guestItemListContainerStyle}>
                            <img alt="tell" src={require('@/static/images/tell.svg')} />
                            <div css={guestItemStyle}>{getGuestTel()}</div>
                          </li>
                          <li css={guestItemListContainerStyle}>
                            <img alt="mail" src={require('@/static/images/mail.svg')} />
                            <div css={guestItemStyle}>{getGuestEmail()}</div>
                          </li>
                          <li css={guestItemListLastContainerStyle}>
                            <img alt="access" src={require('@/static/images/access.svg')} />
                            <div css={guestItemStyle}>{getGuestAddr()}</div>
                          </li>
                        </ul>
                        {/* )} */}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Related customers */}
            <div css={rightContainerStyleForReservation}>
              {/* No repeat management plugin */}
              {!hasRepeatPlugin &&
                (customerlink && !isLoading ? (
                  <CustomerLink
                    history={history}
                    customer={customerlink}
                    currentTags={tags}
                    deleteCustomerLink={deleteCustomerLink}
                    isEdit={isEditTag}
                    onCancel={onClickCancelTag}
                  />
                ) : (
                  <SearchCustomerLinkButton setIsOpenModal={setIsOpenSearchCustomerLinkModal} />
                ))}
              {/* Repeat management plugin available */}
              {hasRepeatPlugin &&
                (customerlink ? (
                  <CustomerLink
                    history={history}
                    customer={customerlink}
                    currentTags={tags}
                    deleteCustomerLink={deleteCustomerLink}
                    isEdit={isEditTag}
                    onCancel={onClickCancelTag}
                  />
                ) : (
                  <SuggestCustomer
                    customers={customers}
                    setIsOpenModal={setIsOpenSearchCustomerLinkModal}
                    onSaveCustomerLink={onSaveCustomerLink}
                  />
                ))}
            </div>
          </div>
          {isOpenSearchCustomerLinkModal && (
            <SearchCustomerLinkModal onClose={() => setIsOpenSearchCustomerLinkModal(false)} onSaveCustomerLink={onSaveCustomerLink} />
          )}
          {reservation && (
            <div css={reservationDetailContainerStyle}>
              <div className="default-block">
                {
                  <ReservationList
                    reservation={reservation}
                    onReload={() => {
                      reloadSameTabs()
                      fetchReservation()
                    }}
                    isAlertConfirmedSales={hasConfirmedSales || hasConfirmedDiscount}
                  />
                }
              </div>
              <div className="default-block -multi-info">
                <div className="reservation-info-block">
                  <PlanInfoDetail
                    reservation={reservation}
                    isEdit={isEdit}
                    onChangeReservation={() => {
                      fetchReservation()
                      reloadSameTabs()
                    }}
                    setIsEdit={setIsEdit}
                    onChangeReservationStatus={() => {
                      fetchReservation()
                      setSalesReloadKey(v => v + 1)
                    }}
                  />
                  {isEdit && (
                    <DetailEditInfo
                      reservation={reservation}
                      isEdit={isEdit}
                      setIsEdit={setIsEdit}
                      onSaved={() => {
                        fetchReservation()
                        reloadSameTabs()
                      }}
                    />
                  )}
                </div>
                <div className="reservation-memo-block">
                  <ReservationDetailMemo
                    reservationId={reservation?.reservationId}
                    checkoutDate={reservation?.checkoutDate}
                    onSavedData={() => {
                      fetchReservation()
                      reloadSameTabs()
                    }}
                    customer={customerlink}
                  />
                </div>
              </div>
              <div css={reservationInfoBottomContainerStyle}>
                <div className="default-block">
                  <GuestRoom
                    reservation={reservation}
                    setIsLoading={setIsLoading}
                    onSavedData={() => {
                      fetchReservation()
                      reloadSameTabs()
                    }}
                  />
                </div>
              </div>
              {shouldShowSpace && (
                <ListSpaceForReservation
                  reservationId={reservation.reservationId}
                  checkinDate={reservation.checkinDate}
                  approvedStatus={reservation.approvedStatus}
                />
              )}
            </div>
          )}
          {isReservationUserEditModalOpen && (
            <ReservationUserEditModal
              formValue={{
                userName: reservation?.userName,
                userNameKana: reservation?.userNameKana,
                userTel: reservation?.userTel,
                userMailAddr: reservation?.userMailAddr,
                userAddr: reservation?.userAddr,
                guestName: reservation?.guestName,
                guestNameKana: reservation?.guestNameKana,
                guestTel: reservation?.guestTel,
                guestMailAddr: reservation?.guestMailAddr,
                guestAddr: reservation?.guestAddr,
              }}
              isLoading={isReservationUserEditModalLoading}
              onClickSave={handleClickReservationUserEditSaveButton}
              onClickCancel={handleClickReservationUserEditCancelButton}
            />
          )}
          {/* Check-in Details_Usage Details */}
          {hasSalesManagerPlugin && reservation && (
            <Sales
              isEdit={isEdit}
              reloadKey={salesReloadKey}
              setHasConfirmedSales={setHasConfirmedSales}
              setHasConfirmedDiscount={setHasConfirmedDiscount}
              currentTabReservationId={reservation.reservationId}
              reservationId={reservation.reservationId}
              customer={{
                id: reservation.reservationId,
                guest: { name: reservation?.guestName || reservation?.userName || '' },
              }}
              customerLink={customerlink}
              reservation={reservation}
              onUpdateSales={() => {
                fetchReservation()
                reloadSameTabs()
              }}
            />
          )}
        </div>
      </div>
      <LoadingFull isLoading={isLoading} />
      <UnsavedChangesRouteGuard hasUnsaved={hasUnsaved} unsavedLabels={unsavedLabels} />
    </div>
  )
}

// Reservation/guest information
export const leftTopSectionStyle = css({
  display: 'flex',
  alignItems: 'stretch',
  overflowWrap: 'anywhere',
})

const detailCustomerContainerStyle = css({
  padding: '24px 32px',
  minWidth: 390,
  width: '50%',
  position: 'relative',
  '@media(max-width: 1080px)': {
    paddingInline: 16,
  },
})

const reservationDetailLeftSectionStyle = css({
  minWidth: 96,
  marginTop: 9,
})

const reservationDetailLeftTitleStyle = css({
  color: '#F2A40B',
  letterSpacing: 0.7,
  fontSize: 14,
  fontWeight: 'bold',
})

const reservationDetailLeftNameKanaStyle = css({
  color: '#676767',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: 0.9,
  marginBottom: 8,
  span: {
    marginLeft: 8,
  },
})

const reservationDetailLeftNameStyle = css({
  color: '#272727',
  fontSize: 18,
  fontWeight: 'bold',
  letterSpacing: 0.9,
  span: {
    marginLeft: 8,
  },
})

const guestItemContainerStyle = css({
  margin: '18px 0 0 30px',
})

const reservationDetailLeftguestNameKanaStyle = css(reservationDetailLeftNameKanaStyle, {})

const reservationDetailLeftguestNameStyle = css(reservationDetailLeftNameStyle, {
  marginBottom: 0,
})

const guestItemListContainerStyle = css({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 12,
  lineHeight: 1.4,
  img: {
    width: 18,
    marginRight: 18,
  },
})

const guestItemListLastContainerStyle = css(guestItemListContainerStyle, {
  marginBottom: 0,
})

const guestItemStyle = css({
  fontSize: 14,
  letterSpacing: 0.7,
  color: '#272727',
  lineHeight: 1.4,
})

// edit button
const editableIcon = css({
  textAlign: 'right',
  display: 'block',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
  backgroundColor: '#FFF',
  borderRadius: '50px',
  width: '30px',
  height: '30px',
  float: 'right',
  backgroundImage: `url(${require('@/static/images/edit.svg')})`,
  border: 'solid 1px #cccccc',
})

const reservationInfoBottomContainerStyle = css({
  width: '100%',
})

const nameAndReflectCustomerStyle = css({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  '@media(max-width: 1080px)': {
    justifyContent: 'flex-start',
    '> div': {
      marginRight: 32,
    },
  },
})

const guestInfoContainerStyle = css({
  padding: '0 32px',
  borderBottom: '1px solid #f5f5f5',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  height: 48,
  '@media(max-width: 1080px)': {
    paddingInline: 16,
  },
})
